import { useEffect, useState, useCallback } from "react";
import { Box, Typography, Paper } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { fetchConversations } from "../api/conversations";
import type { ConversationSummary, ConversationFilters } from "../api/conversations";
import ConversationList from "../components/conversations/ConversationList";
import ConversationTranscript from "../components/conversations/ConversationTranscript";

const PAGE_SIZE = 25;

export default function ConversationsPage() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ConversationFilters>({ limit: PAGE_SIZE, offset: 0 });
  const [selected, setSelected] = useState<ConversationSummary | null>(null);

  const load = useCallback(async (f: ConversationFilters) => {
    setLoading(true);
    try {
      const res = await fetchConversations(f);
      setConversations(res.conversations);
      setTotal(res.total);
    } catch (e) {
      console.error("Failed to load conversations", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const handleFilterChange = (partial: Partial<ConversationFilters>) => {
    const next = { ...filters, ...partial, offset: partial.offset ?? 0 };
    setFilters(next);
    setPage(1);
    if (
      partial.status !== undefined ||
      partial.channel !== undefined ||
      partial.outcome !== undefined ||
      partial.sentiment !== undefined ||
      partial.needs_attention !== undefined ||
      partial.risk_flag !== undefined
    ) {
      setSelected(null);
    }
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    setFilters((f) => ({ ...f, offset: (p - 1) * PAGE_SIZE }));
    setSelected(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 48px)", gap: 0 }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexShrink: 0 }}>
        <ChatBubbleOutlineRoundedIcon sx={{ color: "#C9734F", fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A" }}>
          Conversations
        </Typography>
        {!loading && (
          <Typography sx={{ fontSize: "0.82rem", color: "#94A3B8", ml: 0.5 }}>
            {total.toLocaleString()} total
          </Typography>
        )}
      </Box>

      {/* 2-pane layout */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          backgroundColor: "#fff",
          minHeight: 0,
        }}
      >
        {/* Left: conversation list */}
        <Box
          sx={{
            width: selected ? 340 : "100%",
            borderRight: selected ? "1px solid #E2E8F0" : "none",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "width 0.2s ease",
          }}
        >
          <ConversationList
            conversations={conversations}
            total={total}
            loading={loading}
            filters={filters}
            onFilterChange={handleFilterChange}
            selectedId={selected?.analytics_conversation_id ?? null}
            onSelect={setSelected}
            page={page}
            onPageChange={handlePageChange}
            pageSize={PAGE_SIZE}
          />
        </Box>

        {/* Right: transcript panel */}
        {selected && (
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <ConversationTranscript
              conversation={selected}
              onClose={() => setSelected(null)}
            />
          </Box>
        )}

        {/* Empty right panel hint when nothing selected */}
        {!selected && !loading && conversations.length > 0 && (
          <Box sx={{ display: "none" }} />
        )}
      </Paper>
    </Box>
  );
}
